<template>
  <b-card>
    <!-- Header -->
    <div class="d-flex align-items-center justify-content-between flex-wrap mb-2">
      <!-- หัวข้อในหน้าบอกว่าทำอะไรได้ ส่วนเมนูกับ breadcrumb ใช้ชื่อสั้น — ตามหน้าแพ็คเกจอัตราจ่าย -->
      <h4 class="mb-0">
        {{ $t('ตั้งค่าเงื่อนไขการแทงของสาย') }}
      </h4>
      <div
        v-if="head"
        class="d-flex align-items-center flex-wrap"
        style="gap: 0.5rem"
      >
        <b-badge :variant="scopeBadgeVariant">
          {{ $t(scopeLabel) }}
        </b-badge>
        <!-- ปกติหัวสายคือรายที่เลือกอยู่แล้ว ป้ายนี้จึงไม่ขึ้น — ขึ้นเมื่อสายในฐานข้อมูลเพี้ยนเท่านั้น -->
        <b-badge
          v-if="headMismatch"
          variant="light-danger"
        >
          {{ $t('ค่าจะลงที่') }} {{ head }}
        </b-badge>
      </div>
    </div>

    <!-- Control Bar -->
    <div class="condition-toolbar mb-2">
      <div class="toolbar-field">
        <label class="toolbar-label">{{ $t('ตั้งค่าให้สายของ') }}</label>
        <AgentSelector
          v-model="selectedAgent"
          source="transferLineHead"
          :placeholder="$t('เลือกหัวสาย...')"
          :emit-self-as-option="true"
          :auto-select-default="false"
          :clearable="false"
          :aria-label="$t('ตั้งค่าให้สายของ')"
          @change="onAgentChange"
        />
      </div>
      <div class="toolbar-field">
        <label class="toolbar-label">{{ $t('หวย') }}</label>
        <v-select
          v-model="selectedHuay"
          :options="huayOptions"
          :disabled="huayLoading || busy"
          :loading="huayLoading"
          label="name"
          :reduce="h => h.id"
          :clearable="false"
          :placeholder="huayLoading ? $t('กำลังโหลด...') : $t('ทุกหวย')"
          :aria-label="$t('หวย')"
          @input="onHuayChange"
        />
      </div>
      <div class="toolbar-actions">
        <b-button
          v-b-tooltip.hover="{ placement: 'top', boundary: 'window' }"
          variant="outline-primary"
          :disabled="busy || !head || isYeekee"
          :title="copyButtonHint"
          @click="openCopyModal"
        >
          <feather-icon
            icon="CopyIcon"
            class="mr-50"
          />
          {{ $t('คัดลอกไปหวยอื่น') }}
        </b-button>
      </div>
    </div>

    <b-alert
      v-if="errorMessage"
      variant="danger"
      show
      class="mb-2"
    >
      <div class="alert-body">
        {{ errorMessage }}
      </div>
    </b-alert>

    <!-- Empty State -->
    <div
      v-if="!loading && (!selectedAgent || rows.length === 0)"
      class="condition-empty-state text-center text-muted"
    >
      <feather-icon
        icon="SlidersIcon"
        size="24"
        class="mb-50"
      />
      <div>{{ $t(emptyStateMessage) }}</div>
    </div>

    <!-- ตอนโหลดใช้ตารางใบเดิมทุกอย่าง เปลี่ยนแค่เนื้อในช่องที่ต้องรอข้อมูล — หัวตาราง ชื่อประเภท
         เส้นขอบ และความกว้างทุกคอลัมน์จึงเป็นชุดเดียวกับของจริง ไม่ใช่ตารางอีกใบที่พยายามเลียนแบบ
         พอข้อมูลมาถึงจึงไม่มีอะไรขยับสักพิกเซล -->
    <template v-else>
      <!-- คลุมทับตารางเดิมระหว่างบันทึกและโหลดค่ากลับ ไม่ถอดตารางออกจากหน้า —
           หน้ายุบลงเหลือ spinner แล้วเด้งกลับ ทั้งที่ผู้ใช้แค่กดบันทึก คือการขยับที่ไม่มีเหตุผลรองรับ -->
      <b-overlay
        :show="saving || refreshing"
        rounded="sm"
        bg-color="var(--hdg-panel)"
      >
        <!-- Desktop -->
        <div class="d-none d-md-block">
          <b-table
            responsive
            bordered
            :items="tableItems"
            :fields="$tFields(fields)"
            :tbody-tr-class="rowClass"
            class="mb-1"
          >
            <template #cell(type)="data">
              <div class="type-cell">
                {{ $tDisplay(betTypeLabel(data.value, betTypeNames)) }}
                <feather-icon
                  v-if="isLocked(data.item)"
                  v-b-tooltip.hover
                  :title="$t(isYeekee ? 'fourFiveDigit.lockedYeekee' : 'fourFiveDigit.locked')"
                  icon="LockIcon"
                  class="type-lock"
                />
              </div>
            </template>

            <template #cell(min)="data">
              <b-skeleton
                v-if="loading"
                class="condition-skeleton-input"
              />
              <b-form-input
                v-else
                v-model="data.item.min"
                :disabled="isLocked(data.item)"
                type="number"
                size="sm"
                class="text-center"
                :aria-label="$t('แทงขั้นต่ำต่อครั้ง')"
                :placeholder="placeholderOf(data.item, 'effectiveMin')"
                @input="onCellInput"
              />
            </template>

            <template #cell(maxPerTime)="data">
              <b-skeleton
                v-if="loading"
                class="condition-skeleton-input"
              />
              <b-form-input
                v-else
                v-model="data.item.maxPerTime"
                :disabled="isLocked(data.item)"
                type="number"
                size="sm"
                class="text-center"
                :aria-label="$t('เเทงสูงสุดต่อครั้ง')"
                :placeholder="placeholderOf(data.item, 'effectiveMaxPerTime')"
                @input="onCellInput"
              />
            </template>

            <template #cell(maxPerNumber)="data">
              <b-skeleton
                v-if="loading"
                class="condition-skeleton-input"
              />
              <b-form-input
                v-else
                v-model="data.item.maxPerNumber"
                :disabled="isLocked(data.item)"
                type="number"
                size="sm"
                class="text-center"
                :aria-label="$t('แทงสูงสุดต่อเลข')"
                :placeholder="placeholderOf(data.item, 'effectiveMaxPerNumber')"
                @input="onCellInput"
              />
            </template>

            <template #cell(source)="data">
              <b-skeleton
                v-if="loading"
                class="condition-source condition-skeleton-badge"
              />
              <b-badge
                v-else
                class="condition-source"
                :variant="sourceVariant(data.item)"
              >
                {{ $t(sourceLabel(data.item)) }}
              </b-badge>
            </template>

            <template #cell(actions)="data">
              <b-button
                v-b-tooltip.hover
                variant="flat-secondary"
                size="sm"
                class="btn-icon"
                :disabled="isRowEmpty(data.item) || isLocked(data.item)"
                :title="$t('ล้างค่าของแถวนี้')"
                :aria-label="$t('ล้างค่าของแถวนี้')"
                @click="clearRow(data.item)"
              >
                <feather-icon icon="RotateCcwIcon" />
              </b-button>
            </template>
          </b-table>
        </div>

        <!-- Mobile -->
        <div class="condition-mobile-list d-md-none">
          <article
            v-for="row in tableItems"
            :key="row.type"
            class="condition-mobile-card"
            :class="{ 'is-dirty': isRowDirty(row), 'is-invalid-row': invalidTypes.includes(row.type) }"
          >
            <div class="condition-mobile-header">
              <strong>
                {{ $tDisplay(betTypeLabel(row.type, betTypeNames)) }}
                <feather-icon
                  v-if="isLocked(row)"
                  icon="LockIcon"
                  class="ml-25"
                />
              </strong>
              <div class="d-flex align-items-center">
                <b-skeleton
                  v-if="loading"
                  class="condition-source condition-skeleton-badge"
                />
                <b-badge
                  v-else
                  class="condition-source"
                  :variant="sourceVariant(row)"
                >
                  {{ $t(sourceLabel(row)) }}
                </b-badge>
                <b-button
                  variant="flat-secondary"
                  size="sm"
                  class="btn-icon ml-50"
                  :disabled="isRowEmpty(row) || isLocked(row)"
                  :aria-label="$t('ล้างค่าของแถวนี้')"
                  @click="clearRow(row)"
                >
                  <feather-icon icon="RotateCcwIcon" />
                </b-button>
              </div>
            </div>

            <div class="condition-mobile-fields">
              <label :for="`min-${row.type}`">{{ $t('แทงขั้นต่ำต่อครั้ง') }}</label>
              <b-skeleton
                v-if="loading"
                class="condition-skeleton-input"
              />
              <b-form-input
                v-else
                :id="`min-${row.type}`"
                v-model="row.min"
                :disabled="isLocked(row)"
                type="number"
                size="sm"
                class="text-center"
                :placeholder="placeholderOf(row, 'effectiveMin')"
                @input="onCellInput"
              />

              <label :for="`max-time-${row.type}`">{{ $t('เเทงสูงสุดต่อครั้ง') }}</label>
              <b-skeleton
                v-if="loading"
                class="condition-skeleton-input"
              />
              <b-form-input
                v-else
                :id="`max-time-${row.type}`"
                v-model="row.maxPerTime"
                :disabled="isLocked(row)"
                type="number"
                size="sm"
                class="text-center"
                :placeholder="placeholderOf(row, 'effectiveMaxPerTime')"
                @input="onCellInput"
              />

              <label :for="`max-number-${row.type}`">{{ $t('แทงสูงสุดต่อเลข') }}</label>
              <b-skeleton
                v-if="loading"
                class="condition-skeleton-input"
              />
              <b-form-input
                v-else
                :id="`max-number-${row.type}`"
                v-model="row.maxPerNumber"
                :disabled="isLocked(row)"
                type="number"
                size="sm"
                class="text-center"
                :placeholder="placeholderOf(row, 'effectiveMaxPerNumber')"
                @input="onCellInput"
              />
            </div>
          </article>
        </div>
      </b-overlay>

      <!-- Save bar -->
      <div class="condition-save-bar">
        <div class="condition-save-status">
          <span
            v-if="rowError"
            class="text-danger"
          >{{ rowError }}</span>
          <!-- ไม่มีอะไรค้าง = ไม่มีอะไรต้องบอก ปุ่มบันทึกที่ดับอยู่พูดแทนแล้ว -->
          <span v-else-if="dirtyRowCount > 0">
            {{ dirtyRowCount }} {{ $t('แถวที่ยังไม่บันทึก') }}
          </span>
        </div>
        <div class="condition-save-actions">
          <b-button
            variant="flat-secondary"
            :disabled="busy || !isDirty"
            @click="resetRows"
          >
            {{ $t('ยกเลิกการแก้ไข') }}
          </b-button>
          <b-button
            variant="primary"
            :disabled="busy || !head || !isDirty"
            @click="save"
          >
            <b-spinner
              v-if="saving"
              small
              class="mr-50"
            />
            <feather-icon
              v-else
              icon="SaveIcon"
              class="mr-50"
            />
            {{ $t('บันทึก') }}
          </b-button>
        </div>
      </div>
    </template>

    <b-modal
      v-model="showCopyModal"
      :title="$t('คัดลอกไปหวยอื่น')"
      :ok-title="$t('คัดลอก')"
      :cancel-title="$t('ยกเลิก')"
      :ok-disabled="copySelection.length === 0 || copying"
      :busy="copying"
      centered
      size="lg"
      @ok="copyToLotteries"
    >
      <b-alert
        variant="warning"
        show
        class="mb-1"
      >
        <div class="alert-body">
          {{ $t('ค่าเดิมของหวยที่เลือกจะถูกทับด้วยค่าชุดนี้ทั้งหมด') }}
        </div>
      </b-alert>

      <div class="d-flex align-items-center justify-content-between mb-1">
        <b-badge variant="light-primary">
          {{ $t('เลือกแล้ว') }} {{ copySelection.length }}
        </b-badge>
        <b-button
          variant="flat-secondary"
          size="sm"
          :disabled="copySelection.length === 0"
          @click="copySelection = []"
        >
          {{ $t('ล้างที่เลือก') }}
        </b-button>
      </div>

      <div
        v-for="group in copyGroups"
        :key="group.type"
        class="mb-1"
      >
        <div class="d-flex align-items-center mb-25">
          <strong class="mr-1">{{ $t(group.label) }}</strong>
          <b-button
            variant="flat-primary"
            size="sm"
            @click="toggleGroup(group)"
          >
            {{ isGroupSelected(group) ? $t('เอาออกทั้งหมวด') : $t('เลือกทั้งหมวด') }}
          </b-button>
        </div>
        <b-form-checkbox-group
          v-model="copySelection"
          :options="group.options"
          value-field="id"
          text-field="name"
          stacked
          class="copy-group"
        />
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BAlert, BBadge, BButton, BCard, BFormCheckboxGroup, BFormInput, BModal, BOverlay,
  BSkeleton, BSpinner, BTable,
  VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import AgentSelector from '@core/components/agent/AgentSelector.vue'
import lottoService from '@/service/lottoService'
import fourFiveDigitService from '@/service/fourFiveDigitService'
import {
  FOUR_FIVE_DIGIT_TYPES, YEEKEE_ACCESS_KEY, betTypeLabel, huayAccessLists, isFourFiveDigit, isLockedFourFiveDigit,
} from '@/utils/betTypes'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const ALL_HUAY_ID = '__all__'
const YEEKEE_ID = '__yeekee__'

// ชื่อหมวดหวยตาม Huay.Type ฝั่งเซิร์ฟเวอร์ — ใช้เป็นตัวช่วยเลือกในหน้าคัดลอกเท่านั้น ไม่ใช่ชั้นของข้อมูล
const CATEGORY_LABELS = {
  1: 'หวยรัฐบาล',
  2: 'หวยธนาคาร',
  3: 'หวยหุ้นไทย',
  4: 'หวยหุ้นต่างประเทศ',
}

export default {
  components: {
    BAlert,
    BBadge,
    BButton,
    BCard,
    BFormCheckboxGroup,
    BFormInput,
    BModal,
    BOverlay,
    BSkeleton,
    BSpinner,
    BTable,
    vSelect,
    AgentSelector,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      // 4/5 ตัว: { [huayId]: ประเภทที่สายนี้ใช้ได้ } — แถวของประเภทที่สายใช้ไม่ได้ไม่แสดง
      fourFiveDigitAccess: {},
      // { [huayId]: ประเภทที่ติ๊กเปิดในหวยนั้นแล้ว } — แถวที่เห็นแต่ไม่อยู่ในนี้แก้ไม่ได้
      fourFiveDigitEditable: {},
      huayBetTypeNames: [
        'สามตัวบน',
        'สามตัวโต้ด',
        'สามตัวหน้า',
        'สามตัวท้าย',
        'สองตัวบน',
        'สองตัวล่าง',
        'วิ่งบน',
        'วิ่งล่าง',
      ],
      // ยี่กีมี 6 ประเภทและเลขประเภทไม่ตรงกับหวย — ชุดเดียวกับหน้าค่ากลางยี่กี (YeekeeCondition.vue)
      yeekeeBetTypeNames: [
        'สามตัวบน',
        'สามตัวโต้ด',
        'สองตัวบน',
        'สองตัวล่าง',
        'วิ่งบน',
        'วิ่งล่าง',
      ],
      fields: [
        { key: 'type', label: 'ประเภท', class: 'text-nowrap text-center' },
        // ล็อกความกว้างสามคอลัมน์นี้ไว้ที่หัวตาราง ไม่ปล่อยให้ตัดสินจากเนื้อในช่อง —
        // <input> มีความกว้างในตัวของมันเอง ส่วน skeleton ไม่มี ถ้าไม่ล็อกไว้คอลัมน์จะแคบตอนโหลด
        // แล้วถ่างออกตอนข้อมูลมาถึง
        {
          key: 'min', label: 'แทงขั้นต่ำต่อครั้ง', class: 'text-center', thStyle: { width: '20%' },
        },
        {
          key: 'maxPerTime', label: 'เเทงสูงสุดต่อครั้ง', class: 'text-center', thStyle: { width: '20%' },
        },
        {
          key: 'maxPerNumber', label: 'แทงสูงสุดต่อเลข', class: 'text-center', thStyle: { width: '20%' },
        },
        { key: 'source', label: 'ค่าที่ใช้อยู่', class: 'text-center text-nowrap' },
        { key: 'actions', label: '', class: 'text-center' },
      ],
      selectedAgent: null,
      selectedHuay: ALL_HUAY_ID,
      // ค่าที่โหลดข้อมูลอยู่ตอนนี้ — ใช้ย้อนตัวเลือกกลับเมื่อผู้ใช้ไม่ยอมทิ้งค่าที่แก้ค้างไว้
      loadedAgent: null,
      loadedHuay: ALL_HUAY_ID,
      huayList: [],
      huayLoading: false,
      loading: false,
      // โหลดทับตารางที่อยู่บนหน้าแล้ว ต่างจาก loading ที่ยังไม่มีอะไรให้แสดง
      refreshing: false,
      saving: false,
      copying: false,
      head: '',
      rows: [],
      errorMessage: '',
      rowError: '',
      invalidTypes: [],
      showCopyModal: false,
      copySelection: [],
    }
  },
  computed: {
    // ชื่อหวยมาจาก API จึงแปลไม่ได้ — ที่แปลคือสองตัวเลือกคงที่ (ทุกหวย / ยี่กี)
    huayOptions() {
      return this.huayList.map(huay => ({ ...huay, name: this.$tDisplay(huay.name) }))
    },
    selectedHuayId() {
      const id = this.selectedHuay
      return !id || id === ALL_HUAY_ID || id === YEEKEE_ID ? '' : id
    },
    isYeekee() {
      return this.selectedHuay === YEEKEE_ID
    },
    /// แถวที่ตารางแสดงจริง — ตอนโหลดใช้แถวหลอกที่มีจำนวนและเลขประเภทเท่าของจริง
    /// (ทั้งสองอย่างเป็นข้อมูลฝั่งหน้าจอ รู้ก่อนยิง API อยู่แล้ว) ตารางจึงมีขนาดสุดท้ายตั้งแต่วินาทีแรก
    /// saved ตรงกับช่องว่างทั้งสาม แถวหลอกจึงไม่ถูกนับว่าค้างและไม่ติดไฮไลต์ระหว่างรอ
    tableItems() {
      // ซ่อนแค่ตอนแสดง — แถวที่ซ่อนยังอยู่ใน rows จึงถูกส่งกลับตอนบันทึกเหมือนเดิม ค่าที่ตั้งไว้ไม่หาย
      if (!this.loading) {
        return this.rows.filter(row => !isFourFiveDigit(row.type) || this.fourFiveDigitTypes.includes(row.type))
      }
      return this.betTypeNames.map((name, index) => ({
        type: index + 1, min: '', maxPerTime: '', maxPerNumber: '', saved: '||',
      }))
    },
    // กำลังทำงานอยู่ ไม่ว่าจะโหลดครั้งแรก โหลดทับ หรือบันทึก — ห้ามรับคำสั่งใหม่ทับทั้งสามกรณี
    busy() {
      return this.loading || this.refreshing || this.saving
    },
    betTypeNames() {
      return this.isYeekee ? this.yeekeeBetTypeNames : this.huayBetTypeNames
    },
    // ประเภท 4/5 ตัวที่สายนี้ใช้ได้ในขอบเขตที่เลือก (ทุกหวย = ใช้ได้ในหวยปกติหวยใดหวยหนึ่ง)
    fourFiveDigitTypes() {
      const lists = this.fourFiveDigitScopeLists(this.fourFiveDigitAccess)
      return FOUR_FIVE_DIGIT_TYPES.filter(type => lists.some(list => list.includes(type)))
    },
    // ประเภท 4/5 ตัวที่แก้ได้ในขอบเขตที่เลือก (ทุกหวย = ติ๊กเปิดในหวยปกติหวยใดหวยหนึ่ง)
    fourFiveDigitEditableTypes() {
      const lists = this.fourFiveDigitScopeLists(this.fourFiveDigitEditable)
      return FOUR_FIVE_DIGIT_TYPES.filter(type => lists.some(list => list.includes(type)))
    },
    // ค้างหรือไม่ ตัดสินจากแถวเดียวกับที่ตารางไฮไลต์ — ไม่งั้นแถบล่างบอกว่ามีแถวค้าง
    // แต่ปุ่มบันทึกดับ (แถวที่พิมพ์ยังไม่ครบสามช่องไม่เข้า payload)
    isDirty() {
      return this.dirtyRowCount > 0
    },
    dirtyRowCount() {
      return this.rows.filter(row => this.isRowDirty(row)).length
    },
    scopeLabel() {
      if (this.isYeekee) return 'ยี่กีทุกห้องของสาย'
      return this.selectedHuayId ? 'เฉพาะหวยนี้' : 'ทุกหวยของสาย'
    },
    scopeBadgeVariant() {
      return this.selectedHuayId ? 'light-info' : 'light-secondary'
    },
    copyButtonHint() {
      if (this.isYeekee) return this.$t('ยี่กีไม่มีหวยอื่นให้คัดลอกไป')
      return this.$t('ทับค่าของหวยที่เลือกด้วยค่าชุดนี้')
    },
    emptyStateMessage() {
      if (!this.selectedAgent) return 'เลือกหัวสายเพื่อตั้งค่า'
      return 'ไม่มีข้อมูล'
    },
    // ตัวเลือกมีแต่หัวสาย ค่าที่เซิร์ฟเวอร์ตอบกลับจึงต้องเป็นรายเดียวกันเสมอ
    // ต่างกันเมื่อไหร่แปลว่าสายในฐานข้อมูลไม่ตรงกับที่ Owner ชี้ ต้องเห็นทันทีไม่ใช่รู้ทีหลัง
    headMismatch() {
      return !!this.head && this.head !== this.selectedAgent?.username
    },
    copyGroups() {
      return Object.keys(CATEGORY_LABELS)
        .map(type => ({
          type: Number(type),
          label: CATEGORY_LABELS[type],
          options: this.huayOptions.filter(
            h => h.type === Number(type) && h.id !== this.selectedHuay,
          ),
        }))
        .filter(group => group.options.length > 0)
    },
  },
  async mounted() {
    await this.loadHuayOptions()
    await this.restoreFromQuery()
  },
  methods: {
    betTypeLabel,
    // สิทธิ์ 4/5 ตัวของขอบเขตที่เลือก — ยี่กีทุกห้องใช้ชุดเดียวกัน · มุมมองทุกหวยไม่รวมยี่กี (ยี่กีมีมุมมองของตัวเอง)
    fourFiveDigitScopeLists(access) {
      if (this.isYeekee) return [access[YEEKEE_ACCESS_KEY] || []]
      return this.selectedHuayId ? [access[this.selectedHuayId] || []] : huayAccessLists(access)
    },
    async loadHuayOptions() {
      this.huayLoading = true
      try {
        const res = await lottoService.getAuthen('huay/dict')
        const options = [{ id: ALL_HUAY_ID, name: 'ทุกหวย' }]
        if (res.status === 200) {
          // หวยเลขชุด (type 5) ไม่รับ — Min ของเส้นนั้นเป็นราคาต่อชุด ไม่ใช่ยอดขั้นต่ำ
          options.push(...res.data.filter(h => h.type !== 5))
        }
        options.push({ id: YEEKEE_ID, name: 'ยี่กี (ทุกห้อง)' })
        this.huayList = options
        this.selectedHuay = ALL_HUAY_ID
        this.loadedHuay = ALL_HUAY_ID
      } catch (error) {
        this.toastError(this.$t('ไม่สามารถโหลดรายการหวยได้'))
      } finally {
        this.huayLoading = false
      }
    },

    /// กู้สายกับหวยจาก URL — เปิดหน้าโดยไม่มีพารามิเตอร์ต้องไม่เลือกอะไรให้เอง
    /// ค่าที่โผล่มาเองคือค่าที่ไม่มีใครตั้งใจเลือก และหน้านี้เขียนทับเงื่อนไขทั้งสาย
    async restoreFromQuery() {
      const { agent, huay } = this.$route.query
      if (!agent) return

      // หวยที่ไม่มีในรายการ (ถูกลบไปแล้ว หรือลิงก์มั่ว) ให้ตกลงมาที่ทุกหวย ไม่ใช่ค้างที่ค่าที่เลือกไม่ได้
      const restored = this.huayList.some(v => v.id === huay) ? huay : ALL_HUAY_ID
      this.selectedHuay = restored
      this.loadedHuay = restored
      this.selectedAgent = { username: agent, prefix: '', active: true }
      this.loadedAgent = this.selectedAgent

      await this.loadRows()
    },

    /// จำสถานะไว้ที่ URL ไม่ใช่ localStorage — รีเฟรชแล้วอยู่ที่เดิม เปิดหลายแท็บแยกกันได้
    /// ส่งลิงก์ให้กันได้ และไม่มีค่าค้างจากครั้งก่อนโผล่มาเองตอนเปิดหน้าใหม่
    syncQuery() {
      const query = {}
      if (this.selectedAgent?.username) query.agent = this.selectedAgent.username
      if (this.selectedHuay !== ALL_HUAY_ID) query.huay = this.selectedHuay

      // replace ไม่ใช่ push — เปลี่ยนหวยสิบครั้งแล้วต้องกดย้อนกลับสิบทีถึงจะออกจากหน้าได้
      this.$router.replace({ name: 'bet-condition', query }).catch(() => {})
    },

    // สลับสายหรือหวยทั้งที่ยังมีค่าค้าง = ค่าที่พิมพ์ไว้หายโดยไม่มีใครบอก — ต้องถามก่อนเสมอ
    async onAgentChange() {
      if (await this.keepEditing()) {
        this.$nextTick(() => {
          this.selectedAgent = this.loadedAgent
        })
        return
      }
      this.loadedAgent = this.selectedAgent
      this.syncQuery()
      await this.loadRows()
    },

    async onHuayChange() {
      if (await this.keepEditing()) {
        this.$nextTick(() => {
          this.selectedHuay = this.loadedHuay
        })
        return
      }
      this.loadedHuay = this.selectedHuay
      this.syncQuery()
      await this.loadRows()
    },

    /// true = ผู้ใช้เลือกกลับไปแก้ต่อ จึงต้องย้อนตัวเลือกและไม่โหลดใหม่
    async keepEditing() {
      if (!this.isDirty) return false

      const confirmed = await this.$swal({
        dark: true,
        title: this.$t('Are you sure?'),
        text: this.$t('ยังมีค่าที่ยังไม่บันทึก เปลี่ยนตอนนี้ค่าที่แก้ไว้จะหายไป'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ทิ้งการแก้ไข'),
        cancelButtonText: this.$t('แก้ต่อ'),
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      })

      return !confirmed.value
    },

    async loadRows() {
      this.errorMessage = ''
      this.rowError = ''
      this.invalidTypes = []
      const agent = this.selectedAgent?.username
      if (!agent) {
        this.rows = []
        this.head = ''
        return
      }

      // มีตารางอยู่บนหน้าแล้ว = โหลดทับของเดิม ให้ overlay คลุมไว้แทนการถอดตารางออกแล้วใส่กลับ
      // ซึ่งทำให้หน้ายุบแล้วเด้งทุกครั้งที่กดบันทึกหรือสลับหวย
      if (this.rows.length > 0) this.refreshing = true
      else this.loading = true

      try {
        const [res, fourFiveDigitAccess] = await Promise.all([
          lottoService.getAuthen(this.endpoint()),
          fourFiveDigitService.settingAccess(agent),
        ])
        this.fourFiveDigitAccess = fourFiveDigitAccess.visible
        this.fourFiveDigitEditable = fourFiveDigitAccess.editable
        if (res.status !== 200) {
          this.head = ''
          this.rows = []
          this.errorMessage = res.data
          return
        }

        this.head = res.data.head
        this.rows = res.data.rows.map(row => ({
          type: row.type,
          min: row.min === null ? '' : String(row.min),
          maxPerTime: row.maxPerTime === null ? '' : String(row.maxPerTime),
          maxPerNumber: row.maxPerNumber === null ? '' : String(row.maxPerNumber),
          effectiveMin: row.effectiveMin,
          effectiveMaxPerTime: row.effectiveMaxPerTime,
          effectiveMaxPerNumber: row.effectiveMaxPerNumber,
          source: row.source,
          // ค่าที่บันทึกไว้จริง ใช้บอกว่าแถวไหนถูกแก้แล้วแต่ยังไม่บันทึก
          saved: [row.min, row.maxPerTime, row.maxPerNumber]
            .map(v => (v === null ? '' : String(v))).join('|'),
        }))
      } catch (error) {
        this.head = ''
        this.rows = []
        this.errorMessage = this.$t('ไม่สามารถโหลดข้อมูลได้')
      } finally {
        this.loading = false
        this.refreshing = false
      }
    },

    // ชั้นที่กำลังดูตัดสินจาก endpoint ตัวเดียว — โหลดกับบันทึกจึงชี้ชั้นเดียวกันเสมอ
    endpoint() {
      const params = new URLSearchParams({ agent: this.selectedAgent.username })
      if (this.isYeekee) {
        return `huay-condition-agent/yeekee?${params.toString()}`
      }
      if (this.selectedHuayId) {
        params.set('huayId', this.selectedHuayId)
      }
      return `huay-condition-agent?${params.toString()}`
    },

    // แถวที่กรอกครบสามช่องเท่านั้นที่ถูกส่ง — แถวที่ว่างคือการล้างค่าของชั้นนี้ออก
    payload() {
      return this.rows
        .filter(row => this.isRowFilled(row))
        .map(row => ({
          type: row.type,
          min: Number(row.min),
          maxPerTime: Number(row.maxPerTime),
          maxPerNumber: Number(row.maxPerNumber),
        }))
    },

    isRowEmpty(row) {
      return this.cellsOf(row).every(v => v === '')
    },

    isRowFilled(row) {
      return this.cellsOf(row).every(v => v !== '' && Number(v) > 0)
    },

    cellsOf(row) {
      return [row.min, row.maxPerTime, row.maxPerNumber].map(v => (v === null || v === undefined ? '' : String(v).trim()))
    },

    onCellInput() {
      this.rowError = ''
      this.invalidTypes = []
    },

    clearRow(row) {
      const target = row
      target.min = ''
      target.maxPerTime = ''
      target.maxPerNumber = ''
      this.onCellInput()
    },

    // คืนค่าที่บันทึกไว้ล่าสุดโดยไม่ต้องยิง API ใหม่ — ค่าที่บันทึกแล้วติดอยู่กับแถวอยู่แล้ว
    resetRows() {
      this.rows.forEach(row => {
        const target = row
        const [min, maxPerTime, maxPerNumber] = row.saved.split('|')
        target.min = min
        target.maxPerTime = maxPerTime
        target.maxPerNumber = maxPerNumber
      })
      this.onCellInput()
    },

    placeholderOf(row, key) {
      const value = row[key]
      if (value === null || value === undefined) {
        return this.$t('ตามค่ากลางของหวยนั้น')
      }
      return String(value)
    },

    // ป้ายบอกค่าที่ "บันทึกแล้ว" เท่านั้น — ค่าที่เพิ่งพิมพ์จะขึ้นป้ายยังไม่บันทึกแทน
    isRowDirty(row) {
      return this.cellsOf(row).join('|') !== row.saved
    },

    isLocked(row) {
      return !this.loading && isLockedFourFiveDigit(row.type, this.fourFiveDigitEditableTypes)
    },

    rowClass(row) {
      if (!row) return ''
      if (this.invalidTypes.includes(row.type)) return 'table-danger'
      return this.isRowDirty(row) ? 'condition-row-dirty' : ''
    },

    sourceLabel(row) {
      if (this.isRowDirty(row)) return 'ยังไม่บันทึก'
      if (row.source === 'line-huay') return 'หวยนี้'
      if (row.source === 'line-all') return 'ทุกหวยของสาย'
      return 'ค่ากลาง'
    },

    sourceVariant(row) {
      if (this.isRowDirty(row)) return 'light-warning'
      return row.source === 'global' ? 'light-secondary' : 'light-primary'
    },

    // ชี้แถวที่ผิดด้วย ไม่ใช่บอกแค่ว่าผิด — ตารางแปดแถวหาเองไม่ไหวว่าแถวไหน
    fail(row, message) {
      this.invalidTypes = [row.type]
      this.rowError = this.$t(message)
      return false
    },

    validate() {
      const broken = this.rows.find(row => !this.isRowEmpty(row) && !this.isRowFilled(row))
      if (broken) return this.fail(broken, 'กรอกให้ครบทั้งสามช่อง และต้องมากกว่า 0')

      const inverted = this.rows.find(
        row => this.isRowFilled(row) && Number(row.min) > Number(row.maxPerTime),
      )
      if (inverted) return this.fail(inverted, 'แทงขั้นต่ำต่อครั้งต้องไม่มากกว่าแทงสูงสุดต่อครั้ง')

      // ขั้นต่ำสูงกว่าเพดานต่อเลข = ประเภทนั้นแทงไม่ได้เลยสักครั้ง โดยข้อความที่ลูกค้าเห็นจะชี้ไปที่ประวัติการแทง
      const overCap = this.rows.find(
        row => this.isRowFilled(row) && Number(row.min) > Number(row.maxPerNumber),
      )
      if (overCap) return this.fail(overCap, 'แทงขั้นต่ำต่อครั้งต้องไม่มากกว่าแทงสูงสุดต่อเลข')

      this.onCellInput()
      return true
    },

    async save() {
      if (!this.validate()) return

      this.saving = true
      try {
        const res = await lottoService.putAuthen(this.endpoint(), this.payload())
        if (res.status === 200) {
          this.toastSuccess(this.$t('บันทึกข้อมูสำเร็จ'))
          await this.loadRows()
        } else {
          this.toastError(res.data)
        }
      } catch (error) {
        this.toastError(this.$t('ไม่สามารถบันทึกข้อมูลได้'))
      } finally {
        this.saving = false
      }
    },

    openCopyModal() {
      this.copySelection = []
      this.showCopyModal = true
    },

    isGroupSelected(group) {
      return group.options.every(option => this.copySelection.includes(option.id))
    },

    toggleGroup(group) {
      const ids = group.options.map(option => option.id)
      if (this.isGroupSelected(group)) {
        this.copySelection = this.copySelection.filter(id => !ids.includes(id))
        return
      }
      this.copySelection = [...new Set([...this.copySelection, ...ids])]
    },

    async copyToLotteries(event) {
      event.preventDefault()
      if (this.isDirty) {
        this.toastError(this.$t('บันทึกค่าที่แก้ไว้ก่อนคัดลอก'))
        return
      }

      // ชั้นต้นทางว่าง = คัดลอกแล้วปลายทางถูกล้างทั้งหมด ต้องให้ยืนยันก่อนเพราะย้อนกลับไม่ได้
      if (this.rows.every(row => this.isRowEmpty(row))) {
        const confirmed = await this.$swal({
          dark: true,
          title: this.$t('Are you sure?'),
          text: this.$t('ค่าชุดนี้ยังว่าง คัดลอกแล้วค่าของหวยที่เลือกจะถูกล้างทั้งหมด'),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: this.$t('Ok'),
          cancelButtonText: this.$t('ยกเลิก'),
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-danger ml-1',
          },
          buttonsStyling: false,
        })
        if (!confirmed.value) return
      }

      this.copying = true
      try {
        const params = new URLSearchParams({ agent: this.selectedAgent.username })
        if (this.selectedHuayId) {
          params.set('huayId', this.selectedHuayId)
        }
        const res = await lottoService.postAuthen(
          `huay-condition-agent/copy?${params.toString()}`,
          { huayIds: this.copySelection },
        )
        if (res.status === 200) {
          this.showCopyModal = false
          this.toastSuccess(`${this.$t('คัดลอกสำเร็จ')} ${this.copySelection.length} ${this.$t('หวย')}`)
        } else {
          this.toastError(res.data)
        }
      } catch (error) {
        this.toastError(this.$t('คัดลอกไม่สำเร็จ'))
      } finally {
        this.copying = false
      }
    },

    toastSuccess(text) {
      this.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: {
          title: this.$t('สำเร็จ'), icon: 'CoffeeIcon', variant: 'success', text,
        },
      })
    },

    toastError(text) {
      this.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: {
          title: this.$t('เกิดข้อผิดพลาด'), icon: 'AlertCircleIcon', variant: 'danger', text,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
// ไอคอนล็อกวางทับขอบขวาของช่อง ไม่ดันชื่อประเภท แถวที่ล็อกจึงตรงกับแถวอื่น
.type-cell {
  position: relative;
}

.type-lock {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
}

@import '@core/scss/vue/libs/vue-select.scss';

.condition-toolbar {
  display: grid;
  grid-template-columns: minmax(240px, 1.3fr) minmax(200px, 1fr) auto;
  align-items: end;
  gap: 0.75rem;
}

.toolbar-field {
  min-width: 0;
}

.toolbar-label {
  display: block;
  margin-bottom: 0.35rem;
  color: var(--hdg-muted);
  font-size: 0.78rem;
  font-weight: 500;
}

.toolbar-actions {
  display: flex;
  gap: 0.5rem;

  .btn {
    white-space: nowrap;
  }
}

// ป้ายนี้เปลี่ยนข้อความทุกครั้งที่พิมพ์ (ค่ากลาง ↔ ยังไม่บันทึก) ความกว้างจึงต้องตายตัว
// ไม่ใช่แค่มีขั้นต่ำ — ขั้นต่ำกันไม่ให้แคบลง แต่ไม่กันไม่ให้กว้างขึ้น คอลัมน์ก็ยังโตตามข้อความอยู่ดี
.condition-source {
  display: inline-block;
  width: 7.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
}

// ค่าเริ่มต้นของ b-skeleton มี margin ใต้ตัว และใช้ rgba(0,0,0,.12) ซึ่งเข้มกว่าเส้นคั่นของธีม
// margin ทำให้กล่องที่มันอยู่สูงไม่เท่าตอนสลับกลับเป็นของจริง ส่วนสีทำให้แถบเทาดูหนักไปบนพื้นสว่าง
// --hdg-border เปลี่ยนตามโหมดมืดให้เอง จึงไม่ต้องเขียนสีแยกสองชุด
.b-skeleton {
  margin: 0;
  background-color: var(--hdg-border);
}

// คลื่นวิ่งของ skeleton ฝังสีขาว 40% มาตายตัวในไลบรารี ซึ่งบนพื้นสว่างแทบไม่เห็น
// แต่บนพื้นมืดกลายเป็นแถบสว่างวาบผ่านทุกแถว ต้องหรี่ลงเฉพาะโหมดมืด
.dark-layout .b-skeleton::after {
  background: linear-gradient(90deg, transparent, hsla(0, 0%, 100%, 0.06), transparent);
}

// skeleton ต้องกินพื้นที่เท่าของจริงเป๊ะ ๆ ทั้งกว้างและสูง ไม่งั้นคอลัมน์ขยับตอนข้อมูลมาถึง
// 2.142rem = $input-height-sm ของธีม · ความกว้างปล่อยเต็มช่องเหมือน form-control
.condition-skeleton-input {
  height: 2.142rem;
}

// ความกว้าง 7.5rem มาจาก .condition-source อยู่แล้ว เหลือความสูงให้เท่าป้ายจริง
// = ตัวอักษร 0.85rem ที่ line-height 1 + padding บนล่างอย่างละ 0.3rem
.condition-skeleton-badge {
  height: 0.85rem + 0.6rem;
  vertical-align: middle;
}

.condition-empty-state {
  display: flex;
  min-height: 110px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  border: 1px dashed var(--hdg-border);
  border-radius: 0.428rem;
  background: var(--hdg-panel);
}

::v-deep .condition-row-dirty > td {
  background: rgba(255, 159, 67, 0.08);
}

.condition-mobile-list {
  display: grid;
  gap: 0.75rem;
}

.condition-mobile-card {
  padding: 0.85rem;
  border: 1px solid var(--hdg-border);
  border-radius: 0.5rem;
  background: var(--hdg-panel);

  &.is-dirty {
    border-color: rgba(255, 159, 67, 0.5);
  }

  &.is-invalid-row {
    border-color: var(--hdg-danger);
  }
}

.condition-mobile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  gap: 0.5rem;
}

.condition-mobile-fields {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 0.4rem 0.75rem;

  label {
    margin-bottom: 0;
    color: var(--hdg-muted);
    font-size: 0.78rem;
  }

  .form-control {
    width: 8rem;
  }
}

.condition-save-bar {
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding: 0.65rem 0;
  border-top: 1px solid var(--hdg-border);
  // ต้องทึบ ไม่งั้นแถวตารางจะไหลผ่านแถบนี้ตอนเลื่อน · --hdg-panel เปลี่ยนตามโหมดมืดให้เอง
  background: var(--hdg-panel);
}

.condition-save-status {
  font-size: 0.85rem;
  color: var(--hdg-muted);
}

.condition-save-actions {
  display: flex;
  gap: 0.5rem;
  margin-left: auto;
}

.copy-group {
  columns: 2;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-sweetalert.scss';
</style>
